/** 단체판매 진행(61) — 레거시 appr_edit_forms_groupsales */
export const GROUP_SALES_TYPE = 61;

export const isGroupSalesType = (apvType: number): boolean =>
  apvType === GROUP_SALES_TYPE;

export const GROUP_SALES_MISSING_MESSAGE =
  '단체 할인권 발행 정보가 선택되지 않았습니다.';

export type GroupSalesCandidate = {
  label: string | null;
  value: string | null;
  sequence: string | null;
  shopCode: string | null;
  shopName: string | null;
  brandCode: string | null;
  brandNameEn: string | null;
};

/** EAI Info1 — saleType 'B' 기간단체, 그 밖은 금액단체 */
export type GroupSalesHeader = {
  shopCode?: string | null;
  shopName?: string | null;
  brandNameEn?: string | null;
  approvalStatus?: string | null;
  saleType?: string | null;
  saleTypeName?: string | null;
  saleTypeCount?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
  dateGap?: string | null;
  requestDate?: string | null;
  organizationName?: string | null;
  maxDiscountAmount?: string | null;
  balanceTypeName?: string | null;
  clientName?: string | null;
  contact?: string | null;
};

export type GroupSalesPeriodRow = {
  businessNumber?: string | null;
  organizationName?: string | null;
  count?: string | null;
  amount?: string | null;
  discountRate?: string | null;
  discountAmount?: string | null;
  saleAmount?: string | null;
};

export type GroupSalesAmountRow = {
  quantity?: string | null;
  amount?: string | null;
  discountRate?: string | null;
  discountAmount?: string | null;
  saleAmount?: string | null;
};

/** EAI Info2 — 「상세내역」 판매 품목(금액단체만) */
export type GroupSalesSaleItem = {
  season?: string | null;
  saleTypeName?: string | null;
  partCode?: string | null;
  tagPrice?: string | null;
  unitPrice?: string | null;
  quantity?: string | null;
  amount?: string | null;
  discountRate?: string | null;
  discountAmount?: string | null;
  saleAmount?: string | null;
  remarks?: string | null;
};

/** EAI Info6(금액단체 담당자)·Info7(기간단체 대상자) */
export type GroupSalesParticipant = {
  customerId?: string | null;
  name?: string | null;
  purchaseAmount?: string | null;
  earnedPoint?: string | null;
  progressCount?: string | null;
  discountRate?: string | null;
  lastProgressDate?: string | null;
  organizationName?: string | null;
  shopName?: string | null;
};

export type GroupSalesSummary = {
  shopCode: string | null;
  sequence: string | null;
  header: GroupSalesHeader | null;
  styleCount: string | null;
  periodRows: GroupSalesPeriodRow[];
  amountRows: GroupSalesAmountRow[];
  saleItems?: GroupSalesSaleItem[];
  participants?: GroupSalesParticipant[];
};

export type GroupSalesDraftSource = {
  code: number;
  attributes: {
    brandCode?: string;
    shopCode?: string;
    sequence?: number;
    brandName?: string | null;
  };
  display: {
    subject: string | null;
    candidates: GroupSalesCandidate[] | null;
    summary: GroupSalesSummary | null;
  };
};

/** 「확인」으로 확정한 건 — 저장 attributes 와 같은 모양 */
export type GroupSalesState = {
  brandCode: string;
  brandName: string;
  shopCode: string;
  sequence: number;
  /** 작성 시점 내역표 HTML. 레거시 문서는 없음 */
  content: string | null;
};

const text = (v: unknown): string | null => {
  if (typeof v === 'number') return String(v);
  return typeof v === 'string' && v.trim() ? v : null;
};

/** 저장 attributes → 상태. 신청번호가 없으면 null */
export const parseGroupSalesAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): GroupSalesState | null => {
  const rawSequence = text(attributes?.sequence);
  const sequence = Number(rawSequence);
  const brandCode = text(attributes?.brandCode);
  const shopCode = text(attributes?.shopCode);
  if (
    !attributes ||
    !brandCode ||
    !shopCode ||
    rawSequence === null ||
    !Number.isFinite(sequence)
  )
    return null;
  return {
    brandCode,
    brandName: text(attributes.brandName) ?? '',
    shopCode,
    sequence,
    content: text(attributes.content),
  };
};

export const buildGroupSalesAttributes = (
  state: GroupSalesState | null | undefined,
) =>
  state
    ? {
        brandCode: state.brandCode,
        shopCode: state.shopCode,
        sequence: state.sequence,
        brandName: state.brandName,
        content: state.content,
      }
    : null;

/** 레거시는 임시저장·상신 모두 검사 */
export const checkGroupSalesBeforeSave = (
  state: GroupSalesState | null | undefined,
): string | null =>
  state?.brandCode && state.shopCode && Number.isFinite(state.sequence)
    ? null
    : GROUP_SALES_MISSING_MESSAGE;

export const groupSalesKey = (state: GroupSalesState | null | undefined) =>
  state ? `${state.brandCode}|${state.shopCode}|${state.sequence}` : '';

/** 레거시 상태 문구(APV_STATUS 비면 N) */
export const groupSalesStatusMessage = (
  status: string | null | undefined,
): string => {
  switch (status || 'N') {
    case 'N':
      return '결재할 신규 데이터입니다.';
    case 'I':
      return '결재 진행중인 데이터입니다.';
    case 'Y':
      return '결재완료 후 실행 대기중인 데이터입니다.';
    case 'R':
      return '반려된 데이터입니다.';
    case 'C':
      return '완료 후 할인권 발권된 데이터입니다.';
    default:
      return '알 수 없는 데이터입니다.';
  }
};

export const isGroupSalesPeriodType = (header: GroupSalesHeader | null) =>
  header?.saleType === 'B';

/** 펼침 내역 판정 — 레거시 버튼·BE 데이터 기준(A=담당자·상세, 그 밖=대상자)이라 본표(B=기간) 판정과 다름 */
export const isGroupSalesAmountExpand = (header: GroupSalesHeader | null) =>
  header?.saleType === 'A';

/** 레거시 number_format — 빈 값은 0, 숫자가 아니면 원문 그대로 */
export const formatGroupSalesNumber = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return '0';
  const n = Number(value);
  return Number.isFinite(n)
    ? Math.round(n).toLocaleString('en-US')
    : String(value);
};

/** 후보에 나온 매장을 약칭순으로 — 레거시 combo_shop 표시(약칭 | 매장코드) */
export const buildGroupSalesShopOptions = (
  candidates: GroupSalesCandidate[],
): { value: string; label: string }[] => {
  const shops = new Map<string, string>();
  candidates.forEach((c) => {
    if (c.shopCode && !shops.has(c.shopCode))
      shops.set(c.shopCode, c.shopName ?? '');
  });
  return [...shops.entries()]
    .sort(([, a], [, b]) => a.localeCompare(b, 'ko'))
    .map(([shopCode, shopName]) => ({
      value: shopCode,
      label: `${shopName} | ${shopCode}`,
    }));
};

/** 레거시 담당자/대상자 표 — 연속된 같은 고객은 앞 5칸을 rowspan 으로 묶음 */
export const buildGroupSalesParticipantRows = (
  participants: GroupSalesParticipant[],
) =>
  participants.map((row, i) => {
    const startsGroup =
      i === 0 || participants[i - 1].customerId !== row.customerId;
    let rowSpan = 0;
    if (startsGroup) {
      rowSpan = 1;
      while (
        i + rowSpan < participants.length &&
        participants[i + rowSpan].customerId === row.customerId
      )
        rowSpan += 1;
    }
    return { row, rowSpan, hasHistory: Number(row.progressCount) >= 1 };
  });

type SaleTotals = {
  quantity: number;
  amount: number;
  discountAmount: number;
  saleAmount: number;
};

const emptyTotals = (): SaleTotals => ({
  quantity: 0,
  amount: 0,
  discountAmount: 0,
  saleAmount: 0,
});

/** 레거시 상세내역 — 할인율이 바뀌는 곳마다 「계」, 끝에 「계」·「합계」 */
export const buildGroupSalesSaleItemRows = (items: GroupSalesSaleItem[]) => {
  const rows: (
    | { kind: 'item'; item: GroupSalesSaleItem }
    | { kind: 'subtotal'; totals: SaleTotals }
  )[] = [];
  const total = emptyTotals();
  let sub = emptyTotals();
  items.forEach((item, i) => {
    rows.push({ kind: 'item', item });
    (['quantity', 'amount', 'discountAmount', 'saleAmount'] as const).forEach(
      (key) => {
        const n = Number(item[key]) || 0;
        sub[key] += n;
        total[key] += n;
      },
    );
    const next = items[i + 1];
    if (next && next.discountRate !== item.discountRate) {
      rows.push({ kind: 'subtotal', totals: sub });
      sub = emptyTotals();
    }
  });
  rows.push({ kind: 'subtotal', totals: sub });
  return { rows, total };
};

/** 펼침 내역까지 저장된 content — 이전 저장분은 조회 때 지금 내역으로 보충 */
export const hasGroupSalesExpandSnapshot = (content: string | null) =>
  !!content && content.includes('group-sales-expand');
